/**
 * One place to turn axios errors into a user-readable string.
 *
 * Why a single helper?
 *   Error toasts like "Could not load X" are useless for debugging — the
 *   user can't tell whether it was a 404, a 500, or a serializer bug.
 *   This helper walks the axios error shape in the order a backend dev
 *   would want them surfaced: network → HTTP status + DRF error body →
 *   generic fallback. Result is short enough for a toast but specific
 *   enough to unblock diagnosis.
 *
 * Usage:
 *   } catch (err) {
 *     toast.error(`Could not load approvals: ${formatApiError(err)}`);
 *   }
 */
import type { AxiosError } from 'axios';
import { toast } from 'sonner';

export function formatApiError(err: unknown, fallback = 'Unknown error'): string {
  if (!err || typeof err !== 'object') return fallback;

  const ax = err as AxiosError<unknown>;

  // Network / CORS / offline — no response at all.
  if (!ax.response) {
    if (ax.code === 'ERR_NETWORK') return 'Network unreachable';
    if (ax.code === 'ECONNABORTED') return 'Request timed out';
    return ax.message || fallback;
  }

  const status = ax.response.status;
  const body = ax.response.data;

  // DRF typically returns either a flat {"detail": "..."} or field-level
  // {"field": ["msg"]}. Surface the first meaningful string so the toast
  // shows "422: academic_year: required" not just "422".
  let summary: string | null = null;

  if (body && typeof body === 'object') {
    const rec = body as Record<string, unknown>;

    // Portal's custom error envelope
    // ({status, error_code, message, errors}) comes first — the `status`
    // field is literally "error" and must be skipped, otherwise the
    // toast reads "500: status: error" instead of the real reason.
    if (typeof rec.message === 'string' && rec.message.trim()) {
      const errs = rec.errors as Record<string, unknown> | undefined;
      // If there's a field-level error that's more specific than the
      // summary message, prefer it (e.g. "academic_year: required").
      if (errs && typeof errs === 'object') {
        for (const [key, val] of Object.entries(errs)) {
          if (Array.isArray(val) && typeof val[0] === 'string') {
            summary = `${key}: ${val[0] as string}`;
            break;
          }
          if (typeof val === 'string') {
            summary = `${key}: ${val}`;
            break;
          }
        }
      }
      if (!summary) summary = rec.message;
    } else if (typeof rec.detail === 'string') {
      summary = rec.detail;
    } else {
      for (const [key, val] of Object.entries(rec)) {
        // Skip the envelope discriminator — "status": "error" is noise.
        if (key === 'status' || key === 'error_code') continue;
        if (typeof val === 'string') {
          summary = `${key}: ${val}`;
          break;
        }
        if (Array.isArray(val) && typeof val[0] === 'string') {
          summary = `${key}: ${val[0] as string}`;
          break;
        }
      }
    }
  } else if (typeof body === 'string' && body.length < 200) {
    summary = body;
  }

  if (summary) return `${status}: ${summary}`;

  // Status-only fallback — meaningful codes get a name.
  const codeLabel: Record<number, string> = {
    400: 'Bad request',
    401: 'Session expired',
    403: 'Not allowed',
    404: 'Not found',
    500: 'Server error',
    502: 'Gateway error',
    503: 'Service unavailable',
  };
  return `${status}${codeLabel[status] ? ` ${codeLabel[status]}` : ''}`;
}

/**
 * Shows one toast.error per field validation error so the student knows
 * exactly which field to fix — e.g. "Company name: This field is required."
 *
 * Behaviour:
 *   • DRF field errors      → "Field name: message"  (snake_case humanised)
 *   • detail / non_field_errors → message verbatim (no field prefix)
 *   • Capped at 4 toasts; a "+N more" tail follows if there are extras
 *   • Falls back to a single generic toast for network / 5xx errors
 *
 * Usage:
 *   } catch (err) {
 *     toastValidationErrors(err, 'Failed to submit internship.');
 *   }
 */
export function toastValidationErrors(
  err: unknown,
  fallback = 'Submission failed. Check the highlighted fields.',
): void {
  const pairs = extractFieldErrors(err);
  if (pairs.length > 0) {
    pairs.slice(0, 4).forEach(({ label, msg }) =>
      toast.error(label ? `${label}: ${msg}` : msg),
    );
    if (pairs.length > 4) {
      toast.error(`…and ${pairs.length - 4} more error(s). Fix the above first.`);
    }
    return;
  }

  // No field errors in the body. Before falling back to a generic message,
  // work out WHICH kind of failure this was — they have different remedies
  // and lumping them together is why "Could not create drive." told a
  // coordinator nothing. Same class of bug as the old single-string
  // geolocation error.
  toast.error(describeRequestFailure(err, fallback));
}

/**
 * Turn an axios failure into something the user can act on.
 *
 * Three genuinely different situations were previously collapsed into one
 * message:
 *
 *   • the request never left the browser  -> connection problem, retry
 *   • 401 / 403                           -> session expired, sign in again
 *   • 5xx                                 -> our fault, retrying won't help
 *
 * A drive-creation failure on 12 Sep produced no server-side log at all --
 * neither the app nor Caddy saw the request -- yet the coordinator was told
 * "Could not create drive.", which reads like a rejection rather than a
 * dropped connection. This function is what makes that distinguishable.
 */
export function describeRequestFailure(err: unknown, fallback: string): string {
  const e = err as { response?: { status?: number }; code?: string; message?: string };

  // axios sets no `response` when the request never completed: offline,
  // DNS failure, TLS failure, connection dropped, or a timeout.
  if (!e?.response) {
    if (e?.code === 'ECONNABORTED' || /timeout/i.test(e?.message ?? '')) {
      return 'The request timed out. Your work has not been saved \u2014 check your connection and press Save again.';
    }
    if (typeof navigator !== 'undefined' && navigator.onLine === false) {
      return 'You appear to be offline. Your work has not been saved \u2014 reconnect and press Save again.';
    }
    return 'Could not reach the server. Your work has not been saved \u2014 check your connection and press Save again.';
  }

  const status = e.response.status ?? 0;
  if (status === 401) {
    return 'Your session expired. Open a new tab, sign in again, then press Save \u2014 your form is still here.';
  }
  if (status === 403) {
    return 'You do not have permission to do this.';
  }
  if (status === 413) {
    return 'That file is too large to upload.';
  }
  if (status === 429) {
    return 'Too many attempts in a short time. Wait a minute and try again.';
  }
  if (status >= 500) {
    return 'The server hit an error. Nothing was saved \u2014 this one is on us, please report it.';
  }
  return fallback;
}

/**
 * Pulls field-level validation errors out of an axios error.
 *
 * The portal wraps DRF errors in an envelope:
 *
 *   {status, error_code, message, errors: {field: ["msg"]}}
 *
 * The real per-field detail lives under `errors`, so a loop over the
 * TOP-LEVEL keys finds only `message` — a pre-joined string like
 * "hr_mobile: Mobile number must be exactly 10 digits". That produced a
 * single toast reading "Message: hr_mobile: ..." and no field ever got
 * flagged, which is how a student ends up blaming whichever box they
 * happened to be typing in. Unwrap `errors` first, and fall back to the
 * bare DRF shape for endpoints that don't use the envelope.
 */
export function extractFieldErrors(
  err: unknown,
): { field: string; label: string; msg: string }[] {
  const body = (err as { response?: { data?: unknown } })?.response?.data;
  const out: { field: string; label: string; msg: string }[] = [];

  if (body && typeof body === 'object' && !Array.isArray(body)) {
    const rec = body as Record<string, unknown>;
    const envelope = rec.errors;
    const source =
      envelope && typeof envelope === 'object' && !Array.isArray(envelope)
        ? (envelope as Record<string, unknown>)
        : rec;
    const pairs = out;

    for (const [key, val] of Object.entries(source)) {
      // Skip envelope-only fields that are never human-facing.
      if (key === 'status' || key === 'error_code' || key === 'errors') continue;
      // `message` duplicates the field detail we just unwrapped.
      if (key === 'message' && source !== rec) continue;

      const msgs: string[] = Array.isArray(val)
        ? val.filter((v): v is string => typeof v === 'string')
        : typeof val === 'string'
        ? [val]
        : [];

      // "detail" and "non_field_errors" are global — no field prefix.
      const isGlobal =
        key === 'detail' || key === 'non_field_errors' || key === 'message';
      for (const msg of msgs) {
        pairs.push({ field: key, label: isGlobal ? '' : _humanKey(key), msg });
      }
    }
  }
  return out;
}

/** "company_name" → "Company name" */
function _humanKey(key: string): string {
  return key.replace(/_/g, ' ').replace(/^\w/, (c) => c.toUpperCase());
}
